import { useEffect } from "react";
import { get, useFormContext } from "react-hook-form";
import { useLatest } from "react-use";

/**
 * Call `onChange` when the user edits the form field `name`, with its new and
 * previous values. Resets and `setValue` calls don't count, so this suits
 * filling in related fields without overwriting stored values when a reused
 * form is reset to show another record.
 */
export const useOnUserChange = <T = unknown>(
  name: string,
  onChange: (value: T, prevValue: T) => void,
) => {
  const { watch, getValues } = useFormContext();
  const onChangeRef = useLatest(onChange);

  useEffect(() => {
    // Track every update, resets included, so `prevValue` is what the user saw
    // before their edit.
    let lastValue = getValues(name);
    const subscription = watch((values, info) => {
      const value = get(values, name);
      const prevValue = lastValue;
      lastValue = value;
      if (info.name === name && info.type === "change") {
        onChangeRef.current(value, prevValue);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues, name, onChangeRef]);
};
